import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon/Icon';
import { ActiveAlert, SavedSearchAlertType } from '../../types/savedSearch.types';
import { ALERT_TYPE_META } from './alertTypeMeta';
import { useVisibleAlerts } from './useVisibleAlerts';
import './MobileAlertsFab.scss';

interface MobileAlertsFabProps {
  alerts: ActiveAlert[];
  onAlertClick?: (searchId: number, count: number) => void;
  /** Place le bouton au-dessus du bouton flottant des paramètres (page Commandes). */
  stackedAboveSettings?: boolean;
  /** Bouton intégré (barre de navigation desktop) au lieu du bouton flottant. */
  inline?: boolean;
}

type Filter = 'all' | SavedSearchAlertType;

const TYPES: SavedSearchAlertType[] = ['critical', 'warning', 'info'];

const formatCount = (n: number) => n.toLocaleString('fr-FR');

const MobileAlertsFab: React.FC<MobileAlertsFabProps> = ({
  alerts,
  onAlertClick,
  stackedAboveSettings = false,
  inline = false,
}) => {
  const { visible, dismiss } = useVisibleAlerts(alerts);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');

  const countsByType = useMemo(() => {
    const acc: Record<SavedSearchAlertType, number> = { critical: 0, warning: 0, info: 0 };
    visible.forEach((a) => {
      acc[a.type] += 1;
    });
    return acc;
  }, [visible]);

  const topType: SavedSearchAlertType | null =
    countsByType.critical > 0
      ? 'critical'
      : countsByType.warning > 0
        ? 'warning'
        : countsByType.info > 0
          ? 'info'
          : null;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  // Plus d'alerte : on ferme et on réinitialise le filtre
  useEffect(() => {
    if (visible.length === 0) {
      setOpen(false);
      setFilter('all');
    } else if (filter !== 'all' && countsByType[filter] === 0) {
      setFilter('all');
    }
  }, [visible.length, filter, countsByType]);

  if (!topType) return null;

  const shown = filter === 'all' ? visible : visible.filter((a) => a.type === filter);
  const groups = TYPES.map((type) => ({
    type,
    items: shown.filter((a) => a.type === type),
  })).filter((g) => g.items.length > 0);
  const totalOrders = visible.reduce((sum, a) => sum + a.count, 0);

  const handlePick = (alert: ActiveAlert) => {
    close();
    onAlertClick?.(alert.searchId, alert.count);
  };

  return (
    <>
      {(() => {
        const trigger = (
          <button
            type="button"
            className={`mobile-alerts-fab mobile-alerts-fab--${topType} ${
              stackedAboveSettings ? 'mobile-alerts-fab--stacked' : ''
            } ${inline ? 'mobile-alerts-fab--inline' : ''}`}
            onClick={() => setOpen(true)}
            title={`${totalOrders} commande${totalOrders > 1 ? 's' : ''} concernée${totalOrders > 1 ? 's' : ''}`}
            aria-label={`Alertes : ${totalOrders} commandes concernées`}
            aria-haspopup="dialog"
          >
            <Icon name={ALERT_TYPE_META[topType].icon} size={inline ? 16 : 18} />
            <span className="mobile-alerts-fab__badge">{formatCount(totalOrders)}</span>
          </button>
        );
        return inline ? trigger : createPortal(trigger, document.body);
      })()}

      {open &&
        createPortal(
          <div className="mobile-alerts-sheet" role="presentation">
            <div className="mobile-alerts-sheet__backdrop" onClick={close} />
            <div
              className="mobile-alerts-sheet__panel"
              role="dialog"
              aria-modal="true"
              aria-label="Alertes"
            >
              <div className="mobile-alerts-sheet__grip" aria-hidden />

              <header className="mobile-alerts-sheet__head">
                <div className={`mobile-alerts-sheet__head-icon mobile-alerts-sheet__tone--${topType}`}>
                  <Icon name={ALERT_TYPE_META[topType].icon} size={18} />
                </div>
                <div className="mobile-alerts-sheet__head-text">
                  <h3>Alertes</h3>
                  <p>
                    {visible.length} alerte{visible.length > 1 ? 's' : ''} · {formatCount(totalOrders)}{' '}
                    commande{totalOrders > 1 ? 's' : ''} concernée{totalOrders > 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="mobile-alerts-sheet__close"
                  onClick={close}
                  aria-label="Fermer"
                >
                  <Icon name="x" size={16} />
                </button>
              </header>

              <div className="mobile-alerts-sheet__filters" role="tablist" aria-label="Filtrer par gravité">
                <button
                  type="button"
                  role="tab"
                  aria-selected={filter === 'all'}
                  className={`mobile-alerts-sheet__chip ${filter === 'all' ? 'is-active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  Toutes <b>{visible.length}</b>
                </button>
                {TYPES.filter((t) => countsByType[t] > 0).map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={filter === t}
                    className={`mobile-alerts-sheet__chip mobile-alerts-sheet__chip--${t} ${
                      filter === t ? 'is-active' : ''
                    }`}
                    onClick={() => setFilter(t)}
                  >
                    <i className="mobile-alerts-sheet__dot" />
                    {ALERT_TYPE_META[t].label} <b>{countsByType[t]}</b>
                  </button>
                ))}
              </div>

              <div className="mobile-alerts-sheet__body">
                {groups.map((group) => (
                  <section key={group.type} className="mobile-alerts-sheet__group">
                    {(filter === 'all' || groups.length > 1) && (
                      <h4 className={`mobile-alerts-sheet__group-title mobile-alerts-sheet__tone--${group.type}`}>
                        {ALERT_TYPE_META[group.type].label}
                      </h4>
                    )}
                    <ul>
                      {group.items.map((alert) => (
                        <li key={alert.searchId} className="mobile-alerts-sheet__row-wrap">
                          <button
                            type="button"
                            className={`mobile-alerts-sheet__row mobile-alerts-sheet__row--${alert.type}`}
                            onClick={() => handlePick(alert)}
                          >
                            <span className="mobile-alerts-sheet__row-icon">
                              <Icon name={ALERT_TYPE_META[alert.type].icon} size={15} />
                            </span>
                            <span className="mobile-alerts-sheet__row-main">
                              <span className="mobile-alerts-sheet__row-title">{alert.searchName}</span>
                              <span className="mobile-alerts-sheet__row-sub">
                                Voir les commandes
                              </span>
                            </span>
                            <span className="mobile-alerts-sheet__row-count">
                              {formatCount(alert.count)}
                            </span>
                            <Icon name="chevron-right" size={12} />
                          </button>
                          <button
                            type="button"
                            className="mobile-alerts-sheet__dismiss"
                            onClick={() => dismiss(alert.searchId, alert.count)}
                            aria-label={`Ignorer l'alerte ${alert.searchName}`}
                            title="Ignorer jusqu'au prochain changement"
                          >
                            <Icon name="x" size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MobileAlertsFab;
